import { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/router';

//* HOC's
import { withLanguageContext, withDataContext } from 'context';

//* Components
import FormContainer from 'components/common/FormContainer';
import Container from 'components/common/Container';
import FormItem from 'components/common/FormItem';
import FormInput from 'components/common/FormInput';
import Button from 'components/common/Button';
import Text from 'components/common/Text';
import Icon from 'components/common/Icon';

//* Style
import LandingFooterStyle from './style';

const LandingFooter = ({ global, selectedLang, translate, subscribe }) => {
	//! Router
	const router = useRouter();

	//! Refs
	const formRef = useRef();

	//! State
	const [isEmailSent, setIsEmailSent] = useState(false);

	//! Page Data
	const globalData = useMemo(() => global?.[selectedLang], [global, selectedLang]);

	//! Store footer links
	const storeFooterLinks = useMemo(() => {
		return globalData?.socials
			? Object.keys(globalData.socials).map((social) => {
					return globalData.socials[social] ? (
						<a
							key={social}
							target={'_blank'}
							aria-label={social}
							className={'socials-item'}
							rel={'noopener noreferrer'}
							href={globalData.socials?.[social]}>
							<Icon
								name={social}
								className={'social-icon'}
							/>
						</a>
					) : null;
			  })
			: null;
	}, [globalData?.socials, router]);

	const onFinish = useCallback((emailAddress) => {
		setIsEmailSent('loading');

		return subscribe(emailAddress)
			.then(() => {
				setIsEmailSent(true);

				setTimeout(() => {
					setIsEmailSent(false);
				}, 3000);
			})
			.catch((err) => {
				setIsEmailSent(false);

				return Promise.reject(err);
			});
	});

	useEffect(() => {
		formRef.current.resetFields();
	}, [router]);

	return globalData ? (
		<LandingFooterStyle>
			<Container>
				<div className='landing-footer-content-wrap'>
					<div className='form-wrap'>
						<Text
							className={'font-ping-bolder subscribe-block-text'}
							// text={'footerSubscribeText'}
							text={'footerMirCardRUVersion'}
						/>
						<FormContainer
							ref={formRef}
							onFinish={onFinish}
							className={`footer-form ${isEmailSent ? 'sending' : ''}`}>
							<FormItem
								className={'footer-form-input'}
								name={'email'}
								validationType={'email'}
								required={true}>
								<FormInput placeholder={translate('footerPlaceholderText')} />
							</FormItem>

							<Button
								color={'ColorBigStone'}
								background={'ColorApricot'}
								hoverBackground={'ColorYellowTan'}
								hoverBorderColor={'ColorYellowTan'}
								hoverColor={'ColorBigStone'}
								text={'subscribe'}
								className={'footer-btn'}
								disabled={isEmailSent === 'loading'}
								isBtnLoad={isEmailSent === 'loading'}
							/>
						</FormContainer>

						{isEmailSent && isEmailSent !== 'loading' ? (
							<Text
								text='subscribeSuccessMessage'
								className='footer-form-success-message successColor pXS font-helvetica-regular'
							/>
						) : null}
					</div>
					<div className='subscribe-block-socials'>{storeFooterLinks}</div>
				</div>

				<div className='line' />
				<div className='lower-footer'>
					<div className='lower-footer-text-content-wrap'>
						<div className='copyright-wrap'>
							<Text
								text={`© ${new Date().getFullYear()}`}
								className={'pXS secondaryColor2 font-helvetica'}
							/>
							&nbsp;
							<Text
								text={'copyrightText'}
								className={'pXS secondaryColor2 font-helvetica'}
							/>
						</div>
						<div className='made-by-wrap'>
							<Text
								text={`madeBy`}
								className={'pXS secondaryColor2 font-helvetica'}
							/>
							&nbsp;
							<a
								href='https://conceptstudio.com/en/'
								rel={'noopener noreferrer'}
								target={'_blank'}>
								<Text
									text={`Concept Studio`}
									className='underline secondaryColor2 font-helvetica pXS'
								/>
							</a>
						</div>
					</div>
				</div>
			</Container>
		</LandingFooterStyle>
	) : null;
};

export default withLanguageContext(withDataContext(LandingFooter, ['global', 'subscribe']), ['translate', 'selectedLang']);
